import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ResourceCard from "../components/ResourceCard";
import { publicService } from "../services/publicService";
import { getSelectedResourceIds } from "../utils/inquirySelection";

export default function VerifyInquiry() {
	const navigate = useNavigate();
	const location = useLocation();
	const [loading, setLoading] = useState(true);
	const [verifying, setVerifying] = useState(false);
	const [verificationCode, setVerificationCode] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);
	const [resources, setResources] = useState([]);

	// Get inquiry data from navigation state
	const inquiryData = location.state?.inquiryData;
	const submissionId = location.state?.submissionId;

	useEffect(() => {
		if (!inquiryData || !submissionId) {
			navigate("/inquiry");
			return;
		}

		// Load selected resources
		const loadResources = async () => {
			try {
				const selectedIds = getSelectedResourceIds();
				if (selectedIds.length > 0) {
					const data = await publicService.getResourcesByIds(selectedIds);
					setResources(data || []);
				}
			} catch {
				console.error("Failed to load resources");
			} finally {
				setLoading(false);
			}
		};

		void loadResources();
	}, [inquiryData, submissionId, navigate]);

	const handleVerify = async (e) => {
		e.preventDefault();

		if (!verificationCode.trim()) {
			setError("Please enter the 6-digit verification code");
			return;
		}

		if (verificationCode.replace(/\D/g, "").length !== 6) {
			setError("Verification code must be 6 digits");
			return;
		}

		setVerifying(true);
		setError("");

		try {
			await publicService.verifyInquiryCode(submissionId, verificationCode);
			setSuccess(true);
		} catch {
			setError("Invalid or expired verification code. Please try again.");
		} finally {
			setVerifying(false);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<Navbar />
				<main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
					<div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-600">
						Loading verification page...
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	if (!inquiryData) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<Navbar />
				<main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
					<div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
						<h2 className="text-xl font-semibold text-gray-900 mb-2">
							Invalid Access
						</h2>
						<p className="text-gray-600 mb-5">
							Please submit an inquiry first to access the verification page.
						</p>
						<Link to="/inquiry" className="btn-primary">
							Go to Inquiry Form
						</Link>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navbar />
			<main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				<div className="mb-8">
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Verify Your Inquiry
					</h1>
					<p className="text-gray-600">
						Check your email for the 6-digit verification code and enter it
						below to complete your inquiry submission.
					</p>
				</div>

				<div className="space-y-8">
					{/* Inquiry Summary */}
					<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
						<h2 className="text-xl font-bold text-gray-900 mb-4">
							Inquiry Summary
						</h2>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Name
								</label>
								<p className="text-gray-900">{inquiryData.name}</p>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Email
								</label>
								<p className="text-gray-900">{inquiryData.email}</p>
							</div>
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Company
								</label>
								<p className="text-gray-900">{inquiryData.company}</p>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Selected Resources
								</label>
								<p className="text-gray-900">
									{inquiryData.resourceIds.length} profiles
								</p>
							</div>
						</div>
						<div className="mt-4">
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Message
							</label>
							<p className="text-gray-900 whitespace-pre-wrap">
								{inquiryData.message}
							</p>
						</div>
					</section>

					{/* Selected Resources */}
					<section>
						<h2 className="text-xl font-bold text-gray-900 mb-4">
							Selected Profiles
						</h2>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							{resources.map((resource) => (
								<ResourceCard
									key={resource.id}
									resource={resource}
									selectable={false}
								/>
							))}
						</div>
					</section>

					{/* Verification Form */}
					<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 max-w-2xl">
						{success ? (
							<div className="text-center py-8">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<svg
										className="w-8 h-8 text-green-600"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
								<h3 className="text-xl font-bold text-gray-900 mb-2">
									Inquiry Verified!
								</h3>
								<p className="text-gray-600 mb-5">
									Your inquiry has been verified and submitted successfully. Our
									team will contact you shortly.
								</p>
								<Link to="/resources" className="btn-primary">
									Back to Resource Directory
								</Link>
							</div>
						) : (
							<form onSubmit={handleVerify} className="space-y-4">
								<h2 className="text-xl font-bold text-gray-900 mb-4">
									Enter Verification Code
								</h2>

								{error && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
										{error}
									</div>
								)}

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										6-Digit Verification Code
									</label>
									<div className="relative">
										<input
											type="text"
											value={verificationCode}
											onChange={(e) =>
												setVerificationCode(
													e.target.value.replace(/\D/g, "").slice(0, 6),
												)
											}
											placeholder="000000"
											maxLength="6"
											className={`w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono font-bold border-2 rounded-lg transition-colors ${
												verificationCode
													? "border-blue-400 bg-blue-50"
													: "border-gray-300 bg-white"
											} focus:outline-none focus:border-blue-500 focus:ring-0`}
											disabled={verifying}
										/>
									</div>
									<p className="mt-2 text-sm text-gray-600">
										Check your email for the 6-digit code sent to{" "}
										<span className="font-medium">{inquiryData.email}</span>
									</p>
								</div>

								<div className="flex gap-3 pt-4">
									<button
										type="submit"
										disabled={verifying || !verificationCode}
										className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{verifying ? "Verifying..." : "Verify Code"}
									</button>
									<Link
										to="/inquiry"
										className="flex-1 px-4 py-2 text-center border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
									>
										Cancel
									</Link>
								</div>

								<p className="text-xs text-gray-600 text-center">
									Did not receive the code?{" "}
									<Link to="/inquiry" className="text-blue-600 hover:underline">
										Go back and resubmit
									</Link>
								</p>
							</form>
						)}
					</section>
				</div>
			</main>
			<Footer />
		</div>
	);
}
